/* eslint-disable import/max-dependencies */
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import React, { useState } from 'react';
import DialogActions from '@mui/material/DialogActions';
import { useTranslate } from '@im/react-localization';
import IconButton from '@mui/material/IconButton';
import Radio from '@mui/material/Radio';
import CloseIcon from '@mui/icons-material/Close';
import FormControlLabel from '@mui/material/FormControlLabel';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import { LineDetails } from '../../hooks/deal-details';
import { GridColDef } from '@mui/x-data-grid';
import {
  DataGrid,
  LoadingOverlay,
  NoRowsOverlay,
} from 'src/components/DataGrid';
import RadioGroup from '@mui/material/RadioGroup';
import { LoadingButton } from '@mui/lab';
import { useSplitAndRemoveUpdateQuote } from '../../hooks/splitAndRemove';
import useSnackBar from 'src/hooks/useSnackBar';

type Props = {
  lineDetails: LineDetails;
  onClose: () => void;
};

export default function SpliteAndRemoveDialog(props: Readonly<Props>) {
  const translate = useTranslate();
  const { showSnackBar } = useSnackBar();
  const updateQuote = useSplitAndRemoveUpdateQuote();

  const [formType, setFormType] = useState<'split' | 'remove'>('split');
  const [splitQuantity, setSplitQuantity] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const dataColumns: GridColDef<LineDetails>[] = [
    { field: 'im_sku', headerName: translate('SKU') },
    { field: 'vendor_name', headerName: translate('vendor name') },
    {
      field: 'dd_available_qty',
      headerName: translate('Available Quantity'),
    },
  ];
  const columns: GridColDef<LineDetails>[] = dataColumns.map((column) => {
    return {
      field: column.field,
      flex: 2,
      headerName: column.headerName,
      renderCell: (item) => item.value,
      sortable: false,
    };
  });

  const onFormTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormType(event.target.value as typeof formType);
    setSplitQuantity('');
    setErrorMessage('');
  };

  const handleQuantityChange = (
    event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const value = event.target.value;
    if (/^$|^\d+$/.test(value)) {
      setSplitQuantity(value);
      if (value === '') {
        setErrorMessage(
          translate('VendorDeals.SplitQuantityNotEmptyErrorMessage')
        );
        return;
      }
      if (Number(value) < 1) {
        setErrorMessage(
          translate('VendorDeals.SplitQuantityLessThanErrorMessage', {
            quantity: 1,
          })
        );
        return;
      }
      if (Number(value) >= Number(props.lineDetails.dd_available_qty)) {
        setErrorMessage(
          translate('VendorDeals.SplitQuantityGreaterThanErrorMessage')
        );
        return;
      }
      setErrorMessage('');
    }
  };

  const onUpdateQuoteClick = () => {
    updateQuote
      .mutateAsync({
        dealId: props.lineDetails.deal_number,
        magicKey: props.lineDetails.ntp_magic_key,
        ...(formType === 'split' && { splitQuantity: Number(splitQuantity) }),
        ...(formType === 'remove' && { operations: ['REMOVE_PRODUCTS'] }),
      })
      .then((response) => {
        if (response.errorCode === null) {
          handleClose();
          showSnackBar({
            message: response.message,
            severity: 'success',
          });
        } else {
          showSnackBar({
            message: response.message,
            severity: 'error',
          });
        }
      })
      .catch(() => {
        showSnackBar({
          message: translate('Common.Error'),
          severity: 'error',
        });
      });
  };

  const handleClose = () => !updateQuote.isLoading && props.onClose();

  return (
    <Dialog
      data-testid="gds-vp-split-and-remove-dialog"
      maxWidth={'md'}
      onClose={handleClose}
      open={true}
    >
      <DialogTitle data-testid="gds-vp-split-and-remove-dialog-title">
        {translate('VendorDeals.SplitQuantityOrRemovalOfProducts')}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Stack paddingLeft={1.2} rowGap={2}>
          <RadioGroup
            data-testid="gds-vp-split-and-remove-radio-group"
            onChange={onFormTypeChange}
            sx={{ gap: 2 }}
            value={formType}
          >
            <Stack>
              <FormControlLabel
                control={<Radio />}
                data-testid="gds-vp-split-and-remove-radio-split"
                label={translate('VendorDeals.SplitQuantity')}
                value="split"
              />

              <Stack paddingLeft={4} spacing={0.25}>
                <Typography variant="body2">
                  {translate('VendorDeals.QuantityToSplit')}
                </Typography>
                <TextField
                  disabled={formType !== 'split'}
                  error={Boolean(errorMessage)}
                  helperText={errorMessage}
                  inputProps={{
                    'data-testid': `gds-vp-split-and-remove-quantity-to-split-input`,
                  }}
                  onChange={handleQuantityChange}
                  size="small"
                  value={splitQuantity}
                />
              </Stack>
            </Stack>
            <FormControlLabel
              control={<Radio />}
              data-testid="gds-vp-split-and-remove-radio-remove"
              label={translate('VendorDeals.RemoveProduct')}
              value="remove"
            />
          </RadioGroup>

          <DataGrid
            autoHeight
            columnBuffer={columns.length}
            columns={columns}
            components={{
              LoadingOverlay: LoadingOverlay,
              NoRowsOverlay: NoRowsOverlay,
            }}
            disableColumnFilter
            disableColumnMenu
            disableColumnSelector
            disableSelectionOnClick
            getRowHeight={(params) => 'auto'}
            getRowId={(row: LineDetails) => row.deal_id}
            hideFooterPagination
            hideFooterSelectedRowCount
            rows={props.lineDetails ? [props.lineDetails] : []}
            sx={{
              '.MuiDataGrid-cell': {
                outline: 'transparent !important',
                overflow: 'hidden',
                padding: '0 20px',
              },
              '.MuiDataGrid-columnHeader--moving': {
                backgroundColor: 'transparent',
              },
              '.MuiDataGrid-columnHeaderCheckbox': {
                padding: '0px',
              },
              '.MuiDataGrid-columnHeaderTitle': {
                whiteSpace: 'normal',
              },
              '.MuiDataGrid-footerContainer>div': {
                flexWrap: 'wrap',
              },
              '.MuiDataGrid-footerContainer>div>div': {
                flex: 1,
              },
              '.MuiDataGrid-row': {
                minHeight: '52px !important',
              },
            }}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <LoadingButton
          data-testid="gds-vp-split-and-remove-update-quote"
          disabled={formType === 'split' && (!splitQuantity || !!errorMessage)}
          loading={updateQuote.isLoading}
          onClick={onUpdateQuoteClick}
          variant="newContained"
        >
          {translate('VendorDeals.UpdateQuote')}
        </LoadingButton>
        <Button
          data-testid="gds-vp-split-and-remove-dialog-close"
          onClick={handleClose}
          variant="newOutlined"
        >
          {translate('Common.Cancel')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
